import React, { useState } from 'react'
import { Formik, useFormikContext } from 'formik'
import * as Yup from 'yup'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import PlusIcon from '@mui/icons-material/Add'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'

import FooterButtons from './FooterButtons'
import StatesAutocomplete from 'components/common/StatesAutocomplete'
import TrashIcon from '@mui/icons-material/Delete'
import usePostingNoticeRequestContext from 'hooks/posting_notice_request/usePostingNoticeRequestContext'

// Validation schema for posting notices
const validationSchema = Yup.object().shape({
  postingNotices: Yup.array().of(
    Yup.object().shape({
      state: Yup.string().required('State is required'),
      quantity: Yup.number()
        .typeError('Quantity must be a number')
        .required('Quantity is required')
        .min(1, 'Quantity must be greater than zero')
        .integer('Quantity must be a whole number'),
    })
  ),
})

function StatesRow({ row, index }) {
  const { handleChange, values, setFieldValue, errors, touched } =
    useFormikContext()
  const [searchValue, setSearchValue] = useState('')

  const stateError = errors?.postingNotices?.[index]?.state
  const quantityError = errors?.postingNotices?.[index]?.quantity
  const stateTouched = touched?.postingNotices?.[index]?.state
  const quantityTouched = touched?.postingNotices?.[index]?.quantity

  return (
    <>
      <Grid item xs={6}>
        <StatesAutocomplete
          label="State"
          placeholder="State"
          size="small"
          fullWidth
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          value={row.state}
          setSelectedOption={(option) => {
            handleChange({
              target: {
                name: `postingNotices.${index}.state`,
                value: option?.abbreviation ?? '',
              },
            })
          }}
          textFieldProps={{
            error: stateTouched && Boolean(stateError),
            helperText: stateTouched && stateError,
          }}
        />
      </Grid>
      <Grid item xs={6}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            label="Quantity"
            placeholder="Quantity"
            size="small"
            type="number"
            fullWidth
            value={row.quantity}
            onChange={(e) => {
              handleChange({
                target: {
                  name: `postingNotices.${index}.quantity`,
                  value: e.target.value,
                },
              })
            }}
            error={quantityTouched && Boolean(quantityError)}
            helperText={quantityTouched && quantityError}
          />
          {index > 0 && (
            <IconButton
              edge="end"
              aria-label="close"
              onClick={() => {
                const newPostingNotices = [...values.postingNotices]
                newPostingNotices.splice(index, 1)
                setFieldValue('postingNotices', newPostingNotices)
              }}
            >
              <TrashIcon />
            </IconButton>
          )}
        </Box>
      </Grid>
    </>
  )
}

function PostingNotices() {
  const { values, setFieldValue } = useFormikContext()

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box>
        <Card
          sx={{
            p: 6,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography variant="h2" mb={3} fontWeight={600} textAlign="center">
            Enter the quantity for each state represented by covered locations
            that you wish to have posting notices included.
          </Typography>
          <Grid container xs={12} spacing={2}>
            {values.postingNotices.map((row, index) => (
              <StatesRow key={index} row={row} index={index} />
            ))}
            <Grid item xs={12}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<PlusIcon />}
                onClick={() => {
                  setFieldValue('postingNotices', [
                    ...values.postingNotices,
                    { state: '', quantity: 0 },
                  ])
                }}
              >
                Add
              </Button>
            </Grid>
          </Grid>
        </Card>
        <FooterButtons />
      </Box>
    </Box>
  )
}

function PostingNoticesWrapper() {
  const { stage, setStage, setPostingNotices, postingNotices } =
    usePostingNoticeRequestContext()

  const validate = (values) => {
    const errors = {}

    if (values.postingNotices && values.postingNotices.length > 1) {
      // Create a map to track which indexes have each state
      const stateIndexes = {}

      values.postingNotices.forEach((item, index) => {
        if (item.state && item.state !== '') {
          if (!stateIndexes[item.state]) {
            stateIndexes[item.state] = []
          }
          stateIndexes[item.state].push(index)
        }
      })

      // Find duplicates and set errors for all indexes with duplicate states
      Object.keys(stateIndexes).forEach((state) => {
        if (stateIndexes[state].length > 1) {
          stateIndexes[state].forEach((index) => {
            if (!errors.postingNotices) {
              errors.postingNotices = []
            }
            if (!errors.postingNotices[index]) {
              errors.postingNotices[index] = {}
            }
            errors.postingNotices[index].state =
              'This state has already been selected'
          })
        }
      })
    }

    return errors
  }

  return (
    <Formik
      initialValues={{ postingNotices }}
      validationSchema={validationSchema}
      validate={validate}
      onSubmit={(values) => {
        setStage(stage + 1)
        setPostingNotices(values.postingNotices)
      }}
    >
      <PostingNotices />
    </Formik>
  )
}

export default PostingNoticesWrapper
